import { supabase } from '../../../lib/supabase';
import { useLanguageStore } from '../stores/languageStore';

export interface LocalizationMetrics {
  language: string;
  totalUsers: number;
  activeUsers7d: number;
  totalSessions: number;
  avgSessionDuration: number;
  questionCompletionRate: number;
  audioUsageRate: number;
  translationQuality: number;
  completionPercentage: number;
}

export interface LanguageUsageEvent {
  userId?: string;
  language: string;
  feature: 'ui' | 'quiz' | 'audio' | 'chat' | 'switch';
  duration: number;
  sessionId?: string;
  metadata?: Record<string, any>;
}

export interface TranslationPerformanceMetrics {
  language: string;
  questionsTranslated: number;
  questionsReviewed: number;
  questionsApproved: number;
  avgQualityScore: number;
  communityContributions: number;
  professionalTranslations: number;
}

export interface RegionalMetrics {
  region: string;
  languages: string[];
  totalUsers: number;
  growthRate: number;
  topCategories: string[];
  avgEngagement: number;
}

export class LocalizationAnalyticsService {
  // Track language usage events
  async trackLanguageUsage(event: LanguageUsageEvent): Promise<void> {
    try {
      // Track in local store
      const { trackLanguageUsage } = useLanguageStore.getState();
      trackLanguageUsage(event.language, event.feature, event.duration);

      // Track in external analytics (Amplitude, etc.)
      this.trackExternalAnalytics('language_usage', {
        language: event.language,
        feature: event.feature,
        duration: event.duration,
        ...event.metadata
      });

    } catch (error) {
      console.error('Failed to track language usage:', error);
    }
  }

  // Get comprehensive localization metrics
  async getLocalizationMetrics(): Promise<LocalizationMetrics[]> {
    return [];
  }

  // Get translation performance metrics
  async getTranslationPerformanceMetrics(): Promise<TranslationPerformanceMetrics[]> {
    try {
      const { data: qualityMetrics, error } = await supabase
        .from('translation_quality_metrics')
        .select('*');

      if (error) throw error;

      const { data: contributionStats, error: contributionError } = await supabase
        .from('translation_contributions')
        .select('language, status');
      
      if (contributionError) {
        console.warn('Failed to fetch contribution stats:', contributionError);
      }

      const performanceMetrics: TranslationPerformanceMetrics[] = [];

      qualityMetrics?.forEach(metric => {
        const contributions = contributionStats?.filter((c: any) => c.language === metric.language) || [];
        const communityContributions = contributions
          .filter((c: any) => c.status === 'approved')
          .length;

        performanceMetrics.push({
          language: metric.language,
          questionsTranslated: metric.total_items,
          questionsReviewed: metric.reviewed_items,
          questionsApproved: metric.approved_items,
          avgQualityScore: metric.avg_quality_score,
          communityContributions,
          professionalTranslations: metric.approved_items - communityContributions
        });
      });

      return performanceMetrics;

    } catch (error) {
      console.error('Failed to get translation performance metrics:', error);
      return [];
    }
  }

  // Get regional usage metrics
  async getRegionalMetrics(): Promise<RegionalMetrics[]> {
    try {
      // This would require additional user location data
      // For now, return mock data based on language regions
      const languageRegions: Record<string, string[]> = {
        'latin_america': ['es', 'pt'],
        'europe': ['fr', 'de', 'it', 'ru'],
        'asia': ['ko', 'tl'],
        'africa': ['fr', 'am'],
        'north_america': ['en']
      };

      const metrics = await this.getLocalizationMetrics();
      const regionalMetrics: RegionalMetrics[] = [];

      Object.entries(languageRegions).forEach(([region, languages]) => {
        const regionMetrics = metrics.filter(m => languages.includes(m.language));
        const totalUsers = regionMetrics.reduce((sum, m) => sum + m.totalUsers, 0);
        const avgEngagement = regionMetrics.reduce((sum, m) => sum + m.avgSessionDuration, 0) / regionMetrics.length;

        if (totalUsers > 0) {
          regionalMetrics.push({
            region,
            languages,
            totalUsers,
            growthRate: 0, // Would need historical data
            topCategories: [], // Would need category usage data
            avgEngagement
          });
        }
      });

      return regionalMetrics;

    } catch (error) {
      console.error('Failed to get regional metrics:', error);
      return [];
    }
  }

  // Track language switch events
  async trackLanguageSwitch(fromLanguage: string, toLanguage: string, reason?: string): Promise<void> {
    try {
      await this.trackLanguageUsage({
        language: toLanguage,
        feature: 'switch',
        duration: 1,
        metadata: {
          from_language: fromLanguage,
          to_language: toLanguage,
          reason
        }
      });

      // Track specific language switch event
      this.trackExternalAnalytics('language_switch', {
        from_language: fromLanguage,
        to_language: toLanguage,
        reason
      });

    } catch (error) {
      console.error('Failed to track language switch:', error);
    }
  }

  // Track audio usage
  async trackAudioUsage(language: string, audioType: 'question' | 'option' | 'explanation', duration: number): Promise<void> {
    try {
      await this.trackLanguageUsage({
        language,
        feature: 'audio',
        duration,
        metadata: {
          audio_type: audioType
        }
      });

    } catch (error) {
      console.error('Failed to track audio usage:', error);
    }
  }

  // Track translation quality feedback
  async trackTranslationFeedback(language: string, translationId: string, rating: number, feedback?: string): Promise<void> {
    try {
      this.trackExternalAnalytics('translation_feedback', {
        language,
        translation_id: translationId,
        rating,
        feedback
      });

    } catch (error) {
      console.error('Failed to track translation feedback:', error);
    }
  }

  // Get language adoption funnel
  async getLanguageAdoptionFunnel(language: string): Promise<{
    discovered: number;
    downloaded: number;
    firstUse: number;
    regularUse: number;
    retention7d: number;
    retention30d: number;
  }> {
    try {
      // This would require tracking user journey through language adoption
      // For now, return estimated data based on usage stats
      const metrics = await this.getLocalizationMetrics();
      const languageMetric = metrics.find(m => m.language === language);

      if (!languageMetric) {
        return {
          discovered: 0,
          downloaded: 0,
          firstUse: 0,
          regularUse: 0,
          retention7d: 0,
          retention30d: 0
        };
      }

      // Estimate funnel based on available data
      const totalUsers = languageMetric.totalUsers;
      const activeUsers = languageMetric.activeUsers7d;

      return {
        discovered: Math.round(totalUsers * 1.5), // Estimate
        downloaded: totalUsers,
        firstUse: Math.round(totalUsers * 0.8),
        regularUse: Math.round(totalUsers * 0.6),
        retention7d: activeUsers,
        retention30d: Math.round(activeUsers * 0.7)
      };

    } catch (error) {
      console.error('Failed to get language adoption funnel:', error);
      return {
        discovered: 0,
        downloaded: 0,
        firstUse: 0,
        regularUse: 0,
        retention7d: 0,
        retention30d: 0
      };
    }
  }

  // Get content performance by language
  async getContentPerformance(language: string): Promise<{
    totalQuestions: number;
    avgCompletionRate: number;
    avgTimePerQuestion: number;
    mostDifficultQuestions: any[];
    easiestQuestions: any[];
    categoryPerformance: any[];
  }> {
    try {
      const { data: questionStats, error } = await supabase
        .from('question_usage_stats')
        .select(`
          question_id,
          is_correct,
          time_taken,
          questions!inner(
            question_translations!inner(question, language)
          )
        `)
        .eq('questions.question_translations.language', language);

      if (error) throw error;

      const totalQuestions = new Set(questionStats?.map(s => s.question_id)).size;
      const totalAttempts = questionStats?.length || 0;
      const correctAttempts = questionStats?.filter(s => s.is_correct).length || 0;
      const avgCompletionRate = totalAttempts > 0 ? (correctAttempts / totalAttempts) * 100 : 0;
      const avgTimePerQuestion = questionStats?.reduce((sum, s) => sum + s.time_taken, 0) / totalAttempts || 0;

      // Calculate question difficulty
      const questionPerformance = new Map();
      questionStats?.forEach(stat => {
        const questionId = stat.question_id;
        if (!questionPerformance.has(questionId)) {
          questionPerformance.set(questionId, {
            questionId,
            attempts: 0,
            correct: 0,
            totalTime: 0
          });
        }
        const perf = questionPerformance.get(questionId);
        perf.attempts++;
        if (stat.is_correct) perf.correct++;
        perf.totalTime += stat.time_taken;
      });

      const questionPerformanceArray = Array.from(questionPerformance.values())
        .map(perf => ({
          ...perf,
          successRate: perf.correct / perf.attempts,
          avgTime: perf.totalTime / perf.attempts
        }))
        .sort((a, b) => a.successRate - b.successRate);

      return {
        totalQuestions,
        avgCompletionRate,
        avgTimePerQuestion,
        mostDifficultQuestions: questionPerformanceArray.slice(0, 10),
        easiestQuestions: questionPerformanceArray.slice(-10).reverse(),
        categoryPerformance: [] // Would need category-specific analysis
      };

    } catch (error) {
      console.error('Failed to get content performance:', error);
      return {
        totalQuestions: 0,
        avgCompletionRate: 0,
        avgTimePerQuestion: 0,
        mostDifficultQuestions: [],
        easiestQuestions: [],
        categoryPerformance: []
      };
    }
  }

  // Generate localization report
  async generateLocalizationReport(): Promise<{
    summary: {
      totalLanguages: number;
      totalUsers: number;
      nonEnglishUsage: number;
      avgTranslationQuality: number;
    };
    languageMetrics: LocalizationMetrics[];
    translationPerformance: TranslationPerformanceMetrics[];
    regionalMetrics: RegionalMetrics[];
    recommendations: string[];
  }> {
    try {
      const [languageMetrics, translationPerformance, regionalMetrics] = await Promise.all([
        this.getLocalizationMetrics(),
        this.getTranslationPerformanceMetrics(),
        this.getRegionalMetrics()
      ]);

      const totalUsers = languageMetrics.reduce((sum, m) => sum + m.totalUsers, 0);
      const nonEnglishUsers = languageMetrics
        .filter(m => m.language !== 'en')
        .reduce((sum, m) => sum + m.totalUsers, 0);
      const nonEnglishUsage = totalUsers > 0 ? (nonEnglishUsers / totalUsers) * 100 : 0;
      const avgTranslationQuality = translationPerformance.reduce((sum, p) => sum + p.avgQualityScore, 0) / translationPerformance.length;

      // Generate recommendations
      const recommendations: string[] = [];
      
      if (nonEnglishUsage < 30) {
        recommendations.push('Focus on marketing in non-English markets to increase international adoption');
      }
      
      if (avgTranslationQuality < 4.0) {
        recommendations.push('Improve translation quality through professional review and community contributions');
      }

      const lowQualityLanguages = translationPerformance.filter(p => p.avgQualityScore < 3.5);
      if (lowQualityLanguages.length > 0) {
        recommendations.push(`Priority languages for quality improvement: ${lowQualityLanguages.map(l => l.language).join(', ')}`);
      }

      return {
        summary: {
          totalLanguages: languageMetrics.length,
          totalUsers,
          nonEnglishUsage,
          avgTranslationQuality
        },
        languageMetrics,
        translationPerformance,
        regionalMetrics,
        recommendations
      };

    } catch (error) {
      console.error('Failed to generate localization report:', error);
      throw error;
    }
  }

  // Private helper methods
  private trackExternalAnalytics(eventName: string, properties: Record<string, any>): void {
    try {
      // Track in Amplitude or other analytics service
      // amplitude.track(eventName, properties);
      console.log('Analytics Event:', eventName, properties);
    } catch (error) {
      console.error('Failed to track external analytics:', error);
    }
  }

  // Batch track multiple events (for performance)
  async batchTrackEvents(events: LanguageUsageEvent[]): Promise<void> {
    try {
      const batchData = events.map(event => ({
        user_id: event.userId,
        language: event.language,
        feature_used: event.feature,
        duration_seconds: event.duration,
        session_id: event.sessionId
      }));

      const { error } = await supabase
        .from('language_usage_stats')
        .insert(batchData);

      if (error) throw error;

    } catch (error) {
      console.error('Failed to batch track events:', error);
    }
  }
}

// Singleton instance
export const localizationAnalyticsService = new LocalizationAnalyticsService();

// React hooks for analytics
export const useLocalizationAnalytics = () => {
  return {
    trackLanguageUsage: localizationAnalyticsService.trackLanguageUsage.bind(localizationAnalyticsService),
    trackLanguageSwitch: localizationAnalyticsService.trackLanguageSwitch.bind(localizationAnalyticsService),
    trackAudioUsage: localizationAnalyticsService.trackAudioUsage.bind(localizationAnalyticsService),
    trackTranslationFeedback: localizationAnalyticsService.trackTranslationFeedback.bind(localizationAnalyticsService),
    getLocalizationMetrics: localizationAnalyticsService.getLocalizationMetrics.bind(localizationAnalyticsService),
    generateLocalizationReport: localizationAnalyticsService.generateLocalizationReport.bind(localizationAnalyticsService)
  };
};
